/**
 * Route Configuration - Maps route keys to their React components
 * Used by App.tsx to dynamically generate multilingual routes
 */
import { lazy, type ComponentType } from 'react';
import { RouteMapping, generalRoutes, practiceAreaRoutes, immigrationRoutes } from './route-mapping';
import {
  otherRoutes, areaRoutes, criminalDefenseRoutes, familyLawRoutes,
  personalInjuryRoutes, realEstateRoutes, businessLawRoutes,
  probateEstateRoutes, taxLawRoutes, blogRoutes
} from './route-mapping-extended';

// Direct imports for critical pages (above the fold)
import Index from '@/pages/Index';
import AboutUs from '@/pages/AboutUs';
import PracticeAreas from '@/pages/PracticeAreas';
import Contact from '@/pages/Contact';
import Booking from '@/pages/Booking';
import Blog from '@/pages/Blog';
import NotFound from '@/pages/NotFound';

// Lazy imports for practice area main pages
import ImmigrationServices from '@/pages/ImmigrationServices';
import CriminalDefense from '@/pages/CriminalDefense';
import PersonalInjury from '@/pages/PersonalInjury';
import FamilyLaw from '@/pages/FamilyLaw';
import TaxLaw from '@/pages/TaxLaw';
import ProbateEstate from '@/pages/ProbateEstate';
import RealEstateLaw from '@/pages/RealEstateLaw';
import BusinessLaw from '@/pages/BusinessLaw';

// Immigration sub-pages
import AsylumServices from '@/pages/immigration/AsylumServices';
import CitizenshipNaturalization from '@/pages/immigration/CitizenshipNaturalization';
import FamilyBasedImmigration from '@/pages/immigration/FamilyBasedImmigration';
import EmploymentBasedImmigration from '@/pages/immigration/EmploymentBasedImmigration';
import DeportationRemovalDefense from '@/pages/immigration/DeportationRemovalDefense';
import Waivers from '@/pages/immigration/Waivers';
import DACA from '@/pages/immigration/DACA';
import K1FianceVisa from '@/pages/immigration/K1FianceVisa';
import VAWAPetitions from '@/pages/immigration/VAWAPetitions';
import HumanitarianParoleTPS from '@/pages/immigration/HumanitarianParoleTPS';
import GreenCardRenewal from '@/pages/immigration/GreenCardRenewal';
import ConsularProcessing from '@/pages/immigration/ConsularProcessing';
import UVisa from '@/pages/immigration/UVisa';
import AdvanceParole from '@/pages/immigration/AdvanceParole';
import ReEntryPermit from '@/pages/immigration/ReEntryPermit';
import EB5InvestorVisa from '@/pages/immigration/EB5InvestorVisa';
import StudentVisas from '@/pages/immigration/StudentVisas';

// Criminal Defense sub-pages
import DWI from '@/pages/criminal-defense/DWI';
import DrugCharges from '@/pages/criminal-defense/DrugCharges';
import AssaultFamilyViolence from '@/pages/criminal-defense/AssaultFamilyViolence';
import ViolentCrimes from '@/pages/criminal-defense/ViolentCrimes';
import TheftRobbery from '@/pages/criminal-defense/TheftRobbery';
import SexCrimes from '@/pages/criminal-defense/SexCrimes';
import ExpunctionNondisclosure from '@/pages/criminal-defense/ExpunctionNondisclosure';

// Personal Injury sub-pages
import AutoAccident from '@/pages/personal-injury/AutoAccident';
import TruckAccident from '@/pages/personal-injury/TruckAccident';
import IndustrialAccident from '@/pages/personal-injury/IndustrialAccident';
import MedicalMalpractice from '@/pages/personal-injury/MedicalMalpractice';

// Family Law sub-pages
import Divorce from '@/pages/family-law/Divorce';
import ChildCustody from '@/pages/family-law/ChildCustody';
import ChildSupport from '@/pages/family-law/ChildSupport';
import CPSDefense from '@/pages/family-law/CPSDefense';
import Adoption from '@/pages/family-law/Adoption';
import CourtOrderModification from '@/pages/family-law/CourtOrderModification';

// Tax sub-pages
import TaxLitigation from '@/pages/tax/TaxLitigation';
import TaxPlanning from '@/pages/tax/TaxPlanning';
import TaxDebtResolution from '@/pages/tax/TaxDebtResolution';

// Probate sub-pages
import ProbateAdministration from '@/pages/probate-estate/ProbateAdministration';
import EstatePlanning from '@/pages/probate-estate/EstatePlanning';
import SpecialNeedsTrust from '@/pages/probate-estate/SpecialNeedsTrust';
import Guardianship from '@/pages/probate-estate/Guardianship';

// Real Estate sub-pages
import BuyingSelling from '@/pages/real-estate/BuyingSelling';
import TitleSearch from '@/pages/real-estate/TitleSearch';
import ContractReview from '@/pages/real-estate/ContractReview';
import AffidavitsHeirship from '@/pages/real-estate/AffidavitsHeirship';
import LienRelease from '@/pages/real-estate/LienRelease';
import LeaseNegotiations from '@/pages/real-estate/LeaseNegotiations';
import Closings from '@/pages/real-estate/Closings';
import ForeclosuresEvictions from '@/pages/real-estate/ForeclosuresEvictions';

// Business Law sub-pages
import BusinessFormation from '@/pages/business-law/BusinessFormation';
import PartnershipAgreements from '@/pages/business-law/PartnershipAgreements';
import TrademarkRegistration from '@/pages/business-law/TrademarkRegistration';
import PurchaseSaleBusiness from '@/pages/business-law/PurchaseSaleBusiness';
import RegisteredAgent from '@/pages/business-law/RegisteredAgent';
import DebtCollection from '@/pages/business-law/DebtCollection';
import InsuranceClaims from '@/pages/business-law/InsuranceClaims';
import NonprofitFormation from '@/pages/business-law/NonprofitFormation';
import BusinessClosing from '@/pages/business-law/BusinessClosing';

// Blog posts
import AsylumProcess2025 from '@/pages/blog/AsylumProcess2025';
import DeportationDefense from '@/pages/blog/DeportationDefense';
import CitizenshipGuide from '@/pages/blog/CitizenshipGuide';
import PoliceRights from '@/pages/blog/PoliceRights';
import PersonalInjuryClaim from '@/pages/blog/PersonalInjuryClaim';
import DivorceTexas from '@/pages/blog/DivorceTexas';

// Other pages
import Tools from '@/pages/Tools';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfUse from '@/pages/TermsOfUse';
import BookingSuccess from '@/pages/booking/Success';

// Area pages
import TheWoodlands from '@/pages/areas/TheWoodlands';
import RiverOaks from '@/pages/areas/RiverOaks';
import Montrose from '@/pages/areas/Montrose';
import Heights from '@/pages/areas/Heights';
import WestUniversity from '@/pages/areas/WestUniversity';
import SugarLand from '@/pages/areas/SugarLand';
import Katy from '@/pages/areas/Katy';
import Midtown from '@/pages/areas/Midtown';
import MedicalCenter from '@/pages/areas/MedicalCenter';
import Memorial from '@/pages/areas/Memorial';
import Cypress from '@/pages/areas/Cypress';
import Uptown from '@/pages/areas/Uptown';
import Downtown from '@/pages/areas/Downtown';
import EnergyCorridor from '@/pages/areas/EnergyCorridor';
import GreaterSouthwest from '@/pages/areas/GreaterSouthwest';
import SpringBranch from '@/pages/areas/SpringBranch';

/**
 * A multilingual route entry: route mapping + component
 */
export interface MultilingualRouteEntry {
  routes: RouteMapping;
  component: ComponentType;
}

/**
 * All multilingual routes in the application.
 * Each entry generates 3 <Route> elements (en, es, vi).
 */
export const multilingualRoutes: MultilingualRouteEntry[] = [
  // === General Pages ===
  { routes: generalRoutes.home, component: Index },
  { routes: generalRoutes.about, component: AboutUs },
  { routes: generalRoutes.practiceAreas, component: PracticeAreas },
  { routes: generalRoutes.contact, component: Contact },
  { routes: generalRoutes.booking, component: Booking },
  { routes: generalRoutes.blog, component: Blog },

  // === Practice Area Main Pages ===
  { routes: practiceAreaRoutes.immigration, component: ImmigrationServices },
  { routes: practiceAreaRoutes.criminal, component: CriminalDefense },
  { routes: practiceAreaRoutes.personalInjury, component: PersonalInjury },
  { routes: practiceAreaRoutes.family, component: FamilyLaw },
  { routes: practiceAreaRoutes.tax, component: TaxLaw },
  { routes: practiceAreaRoutes.probate, component: ProbateEstate },
  { routes: practiceAreaRoutes.realEstate, component: RealEstateLaw },
  { routes: practiceAreaRoutes.business, component: BusinessLaw },

  // === Immigration Sub-Pages ===
  { routes: immigrationRoutes.asylumPage, component: AsylumServices },
  { routes: immigrationRoutes.citizenship, component: CitizenshipNaturalization },
  { routes: immigrationRoutes.familyBased, component: FamilyBasedImmigration },
  { routes: immigrationRoutes.employmentBased, component: EmploymentBasedImmigration },
  { routes: immigrationRoutes.deportation, component: DeportationRemovalDefense },
  { routes: immigrationRoutes.waivers, component: Waivers },
  { routes: immigrationRoutes.daca, component: DACA },
  { routes: immigrationRoutes.k1Visa, component: K1FianceVisa },
  { routes: immigrationRoutes.vawa, component: VAWAPetitions },
  { routes: immigrationRoutes.humanitarianParole, component: HumanitarianParoleTPS },
  { routes: immigrationRoutes.greenCardRenewal, component: GreenCardRenewal },
  { routes: immigrationRoutes.consularProcessing, component: ConsularProcessing },
  { routes: immigrationRoutes.uVisa, component: UVisa },
  { routes: immigrationRoutes.advanceParole, component: AdvanceParole },
  { routes: immigrationRoutes.reentryPermit, component: ReEntryPermit },
  { routes: immigrationRoutes.eb5, component: EB5InvestorVisa },
  { routes: immigrationRoutes.studentVisas, component: StudentVisas },

  // === Criminal Defense Sub-Pages ===
  { routes: criminalDefenseRoutes.dwi, component: DWI },
  { routes: criminalDefenseRoutes.drugCharges, component: DrugCharges },
  { routes: criminalDefenseRoutes.assault, component: AssaultFamilyViolence },
  { routes: criminalDefenseRoutes.violentCrimes, component: ViolentCrimes },
  { routes: criminalDefenseRoutes.theftRobbery, component: TheftRobbery },
  { routes: criminalDefenseRoutes.sexCrimes, component: SexCrimes },
  { routes: criminalDefenseRoutes.expunction, component: ExpunctionNondisclosure },

  // === Personal Injury Sub-Pages ===
  { routes: personalInjuryRoutes.autoAccident, component: AutoAccident },
  { routes: personalInjuryRoutes.truckAccident, component: TruckAccident },
  { routes: personalInjuryRoutes.industrialAccident, component: IndustrialAccident },
  { routes: personalInjuryRoutes.medicalMalpractice, component: MedicalMalpractice },

  // === Family Law Sub-Pages ===
  { routes: familyLawRoutes.divorce, component: Divorce },
  { routes: familyLawRoutes.childCustody, component: ChildCustody },
  { routes: familyLawRoutes.childSupport, component: ChildSupport },
  { routes: familyLawRoutes.cpsDefense, component: CPSDefense },
  { routes: familyLawRoutes.adoption, component: Adoption },
  { routes: familyLawRoutes.courtOrderModification, component: CourtOrderModification },

  // === Tax Sub-Pages ===
  { routes: taxLawRoutes.taxLitigation, component: TaxLitigation },
  { routes: taxLawRoutes.taxPlanning, component: TaxPlanning },
  { routes: taxLawRoutes.taxDebtResolution, component: TaxDebtResolution },

  // === Probate & Estate Sub-Pages ===
  { routes: probateEstateRoutes.probateAdministration, component: ProbateAdministration },
  { routes: probateEstateRoutes.estatePlanning, component: EstatePlanning },
  { routes: probateEstateRoutes.specialNeedsTrust, component: SpecialNeedsTrust },
  { routes: probateEstateRoutes.guardianship, component: Guardianship },

  // === Real Estate Sub-Pages ===
  { routes: realEstateRoutes.buyingSelling, component: BuyingSelling },
  { routes: realEstateRoutes.titleSearch, component: TitleSearch },
  { routes: realEstateRoutes.contractReview, component: ContractReview },
  { routes: realEstateRoutes.affidavitsHeirship, component: AffidavitsHeirship },
  { routes: realEstateRoutes.lienRelease, component: LienRelease },
  { routes: realEstateRoutes.leaseNegotiations, component: LeaseNegotiations },
  { routes: realEstateRoutes.closings, component: Closings },
  { routes: realEstateRoutes.foreclosuresEvictions, component: ForeclosuresEvictions },

  // === Business Law Sub-Pages ===
  { routes: businessLawRoutes.businessFormation, component: BusinessFormation },
  { routes: businessLawRoutes.partnershipAgreements, component: PartnershipAgreements },
  { routes: businessLawRoutes.trademarkRegistration, component: TrademarkRegistration },
  { routes: businessLawRoutes.purchaseSaleBusiness, component: PurchaseSaleBusiness },
  { routes: businessLawRoutes.registeredAgent, component: RegisteredAgent },
  { routes: businessLawRoutes.debtCollection, component: DebtCollection },
  { routes: businessLawRoutes.insuranceClaims, component: InsuranceClaims },
  { routes: businessLawRoutes.nonprofitFormation, component: NonprofitFormation },
  { routes: businessLawRoutes.businessClosing, component: BusinessClosing },

  // === Blog Posts ===
  { routes: blogRoutes.asylumProcess, component: AsylumProcess2025 },
  { routes: blogRoutes.deportationDefense, component: DeportationDefense },
  { routes: blogRoutes.citizenshipGuide, component: CitizenshipGuide },
  { routes: blogRoutes.policeRights, component: PoliceRights },
  { routes: blogRoutes.personalInjuryClaim, component: PersonalInjuryClaim },
  { routes: blogRoutes.divorceTexas, component: DivorceTexas },

  // === Other Pages ===
  { routes: otherRoutes.tools, component: Tools },
  { routes: otherRoutes.privacyPolicy, component: PrivacyPolicy },
  { routes: otherRoutes.termsOfUse, component: TermsOfUse },

  // === Area Pages ===
  { routes: areaRoutes.theWoodlands, component: TheWoodlands },
  { routes: areaRoutes.riverOaks, component: RiverOaks },
  { routes: areaRoutes.montrose, component: Montrose },
  { routes: areaRoutes.heights, component: Heights },
  { routes: areaRoutes.westUniversity, component: WestUniversity },
  { routes: areaRoutes.sugarLand, component: SugarLand },
  { routes: areaRoutes.katy, component: Katy },
  { routes: areaRoutes.midtown, component: Midtown },
  { routes: areaRoutes.medicalCenter, component: MedicalCenter },
  { routes: areaRoutes.memorial, component: Memorial },
  { routes: areaRoutes.cypress, component: Cypress },
  { routes: areaRoutes.uptown, component: Uptown },
  { routes: areaRoutes.downtown, component: Downtown },
  { routes: areaRoutes.energyCorridor, component: EnergyCorridor },
  { routes: areaRoutes.greaterSouthwest, component: GreaterSouthwest },
  { routes: areaRoutes.springBranch, component: SpringBranch },
];

export { NotFound, BookingSuccess };
